import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { format, addMonths, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { ru } from 'date-fns/locale';
import { extractYmd } from '../../utils/dateUtils';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { toast } from 'react-toastify';
import { formatUserDisplayName, displayTrainerName } from '../../utils/userDisplayName';
import { useConfig } from '../../contexts/ConfigContext';
import { getDirectionTerms } from '../../utils/organizationHelpers';
import { formatDateShortRu, formatDateRangeRu } from '../../utils/dateUtils';
import * as XLSX from 'xlsx';
import './CalendarPage.css';

const CalendarPage = () => {
    const { config } = useConfig();
    const directionTerms = getDirectionTerms(config?.code);
    // Начинаем с текущего месяца
    const [periodStart, setPeriodStart] = useState(startOfMonth(new Date()));
    const [selectedTrainerId, setSelectedTrainerId] = useState(null);
    const [trainerSortMode, setTrainerSortMode] = useState('alpha'); // alpha | occupancy
    const [exportAllSchedule, setExportAllSchedule] = useState(false);
    const [exporting, setExporting] = useState(false);

    // Вычисляем период (3 месяца)
    const periodEnd = endOfMonth(addMonths(periodStart, 2));

    const { data: calendarData, isLoading, isFetching } = useQuery(
        ['calendar', periodStart, periodEnd],
        async () => {
            // Добавляем небольшую задержку для лучшего UX
            await new Promise(resolve => setTimeout(resolve, 800));

            const response = await axios.get(`${API_BASE_URL}/schedule/calendar`, {
                withCredentials: true,
                params: {
                    start_date: format(periodStart, 'yyyy-MM-dd'),
                    end_date: format(periodEnd, 'yyyy-MM-dd')
                }
            });
            return response.data;
        },
        {
            keepPreviousData: true, // Сохраняем предыдущие данные при загрузке новых
            staleTime: 10 * 60 * 1000, // Кэшируем на 10 минут
            cacheTime: 30 * 60 * 1000, // Храним в кэше 30 минут
            refetchOnWindowFocus: false, // Не перезагружаем при фокусе окна
            refetchOnMount: false, // Не перезагружаем при монтировании если есть кэш
        }
    );

    const events = useMemo(() => {
        if (!calendarData) return [];
        return Array.isArray(calendarData) ? calendarData : (calendarData.events || []);
    }, [calendarData]);

    const vacations = useMemo(() => {
        if (!calendarData || Array.isArray(calendarData)) return [];
        return calendarData.vacations || [];
    }, [calendarData]);

    const { data: allUsers } = useQuery('trainers', async () => {
        const response = await axios.get(`${API_BASE_URL}/users/trainers`, {
            withCredentials: true
        });
        return response.data;
    });
    // Мемоизируем дни периода
    const days = useMemo(() => {
        return eachDayOfInterval({ start: periodStart, end: periodEnd });
    }, [periodStart, periodEnd]);

    // Переключение периодов
    const goToPrevious3Months = useCallback(() => {
        setPeriodStart(prev => addMonths(prev, -3));
    }, []);

    const goToNext3Months = useCallback(() => {
        setPeriodStart(prev => addMonths(prev, 3));
    }, []);

    const toggleTrainerSelection = useCallback((trainerId) => {
        setSelectedTrainerId(prev => (prev === trainerId ? null : trainerId));
    }, []);

    // Список тренеров с привязанными событиями (без сортировки)
    const trainersBase = useMemo(() => {
        if (!allUsers) return [];

        const trainersMap = new Map();

        allUsers.forEach(user => {
                trainersMap.set(user.id, {
                    id: user.id,
                    name: formatUserDisplayName(user),
                    events: []
                });
            });

        if (events) {
            events.forEach(event => {
                if (event.trainers && Array.isArray(event.trainers)) {
                    event.trainers.forEach(trainer => {
                        if (trainersMap.has(trainer.user_id)) {
                            trainersMap.get(trainer.user_id).events.push(event);
                        }
                    });
                }
            });
        }

        return Array.from(trainersMap.values());
    }, [allUsers, events]);

    // Мемоизируем события по дням для быстрого доступа
    const eventsByDay = useMemo(() => {
        if (!events) return new Map();

        const eventsMap = new Map();
        const parseLocalDate = (value) => {
            const ymd = extractYmd(value);
            if (!ymd) return null;
            return new Date(ymd.year, ymd.month - 1, ymd.day);
        };

        events.forEach(event => {
            const startDate = parseLocalDate(event.start_date);
            const endDate = parseLocalDate(event.end_date);
            if (!startDate || !endDate) return;

            for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                const dayKey = format(d, 'yyyy-MM-dd');
                if (!eventsMap.has(dayKey)) {
                    eventsMap.set(dayKey, []);
                }
                eventsMap.get(dayKey).push(event);
            }
        });
        return eventsMap;
    }, [events]);

    // Занятость: число дней с событиями в выбранном периоде
    const trainerOccupancyDays = useMemo(() => {
        const map = new Map();
        if (!trainersBase.length || !days.length) return map;

        trainersBase.forEach((trainer) => {
            let count = 0;
            for (const day of days) {
                const dayKey = format(day, 'yyyy-MM-dd');
                const dayEvents = eventsByDay.get(dayKey) || [];
                const hasEvent = dayEvents.some(
                    (ev) => ev.trainers && ev.trainers.some((t) => t.user_id == trainer.id)
                );
                if (hasEvent) count++;
            }
            map.set(trainer.id, count);
        });
        return map;
    }, [trainersBase, days, eventsByDay]);

    const trainers = useMemo(() => {
        const list = [...trainersBase];
        if (trainerSortMode === 'occupancy') {
            list.sort((a, b) => {
                const diff = (trainerOccupancyDays.get(b.id) || 0) - (trainerOccupancyDays.get(a.id) || 0);
                if (diff !== 0) return diff;
                return a.name.localeCompare(b.name, 'ru');
            });
        } else {
            list.sort((a, b) => a.name.localeCompare(b.name, 'ru'));
        }
        return list;
    }, [trainersBase, trainerSortMode, trainerOccupancyDays]);

    const toggleTrainerSort = useCallback(() => {
        setTrainerSortMode((prev) => (prev === 'alpha' ? 'occupancy' : 'alpha'));
    }, []);

    const buildScheduleWorkbook = useCallback((eventsList, fileName) => {
        const sorted = [...eventsList].sort((a, b) => {
            const startA = extractYmd(a.start_date);
            const startB = extractYmd(b.start_date);
            if (!startA && !startB) return 0;
            if (!startA) return 1;
            if (!startB) return -1;
            const cmpStart =
                startA.year - startB.year ||
                startA.month - startB.month ||
                startA.day - startB.day;
            if (cmpStart !== 0) return cmpStart;
            const endA = extractYmd(a.end_date);
            const endB = extractYmd(b.end_date);
            if (!endA && !endB) return 0;
            if (!endA) return 1;
            if (!endB) return -1;
            return endA.year - endB.year || endA.month - endB.month || endA.day - endB.day;
        });

        const headers = [
            'Дата начала',
            'Дата окончания',
            'Период',
            'Группа (код)',
            'Название группы',
            directionTerms.one,
            'Регион',
            'Цикл',
            'Тема',
            'Тренеры',
            'Формат'
        ];

        const rows = sorted.map((ev) => {
            const trainerNames = (ev.trainers || [])
                .map((t) => displayTrainerName(t))
                .filter(Boolean)
                .join('; ');
            const region = (ev.region_name || '').toLowerCase();
            const formatLabel =
                region.includes('онлайн') || region.includes('online') ? 'Онлайн' : 'Офлайн';

            return [
                formatDateShortRu(ev.start_date),
                formatDateShortRu(ev.end_date),
                formatDateRangeRu(ev.start_date, ev.end_date),
                ev.group_name || '',
                ev.group_nomination || '',
                ev.direction_name || '',
                ev.region_name || '',
                ev.cycle ?? '',
                ev.theme || '',
                trainerNames,
                formatLabel
            ];
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        XLSX.utils.book_append_sheet(wb, ws, 'Расписание');
        XLSX.writeFile(wb, fileName);
    }, []);

    const handleExportSchedule = useCallback(async () => {
        setExporting(true);
        try {
            let eventsToExport = events;

            if (exportAllSchedule) {
                const response = await axios.get(`${API_BASE_URL}/schedule/calendar`, {
                    withCredentials: true,
                    params: { all: '1' }
                });
                const data = response.data;
                eventsToExport = Array.isArray(data) ? data : (data.events || []);
            }

            if (!eventsToExport?.length) {
                toast.info(
                    exportAllSchedule
                        ? 'Нет событий для выгрузки'
                        : 'Нет событий для выгрузки за выбранный период'
                );
                return;
            }

            const fileName = exportAllSchedule
                ? `расписание_всё_${new Date().toISOString().slice(0, 10)}.xlsx`
                : `расписание_${format(periodStart, 'yyyy-MM-dd')}_${format(periodEnd, 'yyyy-MM-dd')}.xlsx`;

            buildScheduleWorkbook(eventsToExport, fileName);
            toast.success('Выгрузка сохранена');
        } catch (error) {
            console.error('Ошибка выгрузки расписания:', error);
            toast.error(error.response?.data?.message || 'Ошибка выгрузки');
        } finally {
            setExporting(false);
        }
    }, [events, exportAllSchedule, periodStart, periodEnd, buildScheduleWorkbook]);

    // Отпуска по дням
    const vacationsByDay = useMemo(() => {
        const map = new Map();
        if (!vacations) return map;
        const parseLocalDate = (value) => {
            const ymd = extractYmd(value);
            if (!ymd) return null;
            return new Date(ymd.year, ymd.month - 1, ymd.day);
        };
        vacations.forEach(v => {
            const startDate = parseLocalDate(v.start_date);
            const endDate = parseLocalDate(v.end_date);
            if (!startDate || !endDate) return;
            for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                const dayKey = format(d, 'yyyy-MM-dd');
                if (!map.has(dayKey)) map.set(dayKey, []);
                map.get(dayKey).push(v);
            }
        });
        return map;
    }, [vacations]);

    // Проверка занятости тренера в конкретный день (мемоизированная)
    const getTrainerEventForDay = useCallback((trainerId, day) => {
        if (!events) return null;

        const dayKey = format(day, 'yyyy-MM-dd');
        const dayEvents = eventsByDay.get(dayKey) || [];

        return dayEvents.find(event => {
            // Проверяем что тренер участвует в этом событии
            return event.trainers && event.trainers.some(t => t.user_id == trainerId);
        });
    }, [events, eventsByDay]);

    const getTrainerVacationForDay = useCallback((trainerId, day) => {
        const dayKey = format(day, 'yyyy-MM-dd');
        const dayVacations = vacationsByDay.get(dayKey) || [];
        return dayVacations.find(v => v.user_id == trainerId) || null;
    }, [vacationsByDay]);

    // Определяем формат события по месту проведения группы
    const getEventFormat = useCallback((event) => {
        const region = (event?.region_name || '').toLowerCase();
        if (region.includes('онлайн') || region.includes('online')) {
            return 'online';
        }
        return 'offline';
    }, []);

    // Группируем последовательные дни одного события (мемоизированная)
    const getCellSpanForDay = useCallback((trainerId, dayIndex) => {
        const day = days[dayIndex];
        const event = getTrainerEventForDay(trainerId, day);

        if (!event) {
            const vacation = getTrainerVacationForDay(trainerId, day);
            if (vacation) {
                // если предыдущий день тоже отпуск (и нет события), то это продолжение span
                if (dayIndex > 0) {
                    const prevDay = days[dayIndex - 1];
                    const prevEvent = getTrainerEventForDay(trainerId, prevDay);
                    const prevVacation = getTrainerVacationForDay(trainerId, prevDay);
                    if (!prevEvent && prevVacation) {
                        return { skip: true };
                    }
                }

                let span = 1;
                for (let i = dayIndex + 1; i < days.length; i++) {
                    const nextDay = days[i];
                    const nextEvent = getTrainerEventForDay(trainerId, nextDay);
                    const nextVacation = getTrainerVacationForDay(trainerId, nextDay);
                    if (!nextEvent && nextVacation) {
                        span++;
                    } else {
                        break;
                    }
                }

                return { span, vacation: true };
            }
            return null;
        }

        // Проверяем не начинается ли эта ячейка с середины события
        if (dayIndex > 0) {
            const prevDay = days[dayIndex - 1];
            const prevEvent = getTrainerEventForDay(trainerId, prevDay);
            if (prevEvent && prevEvent.id === event.id) {
                return { skip: true }; // Эта ячейка уже включена в предыдущий span
            }
        }

        // Считаем сколько дней подряд длится событие
        let span = 1;
        for (let i = dayIndex + 1; i < days.length; i++) {
            const nextEvent = getTrainerEventForDay(trainerId, days[i]);
            if (nextEvent && nextEvent.id === event.id) {
                span++;
            } else {
                break;
            }
        }

        return { span, event };
    }, [days, getTrainerEventForDay, getTrainerVacationForDay]);

    if (isLoading) {
        return (
            <div className="calendar-page">
                <div className="calendar-header">
                    <div>
                        <h1>
                            <CalendarIcon size={28} />
                            Календарь занятости
                        </h1>
                        <p>Расписание всех тренеров</p>
                    </div>
                </div>
                <div className="loading-container">
                    <div className="loading">
                        <div className="spinner"></div>
                        <p>Загрузка календаря...</p>
                        <p className="loading-subtitle">Пожалуйста, подождите</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="calendar-page">
            <div className="calendar-header">
                <div>
                    <h1>
                        <CalendarIcon size={28} />
                        Календарь занятости
                        {isFetching && <span className="loading-indicator">🔄</span>}
                    </h1>
                    <p>Расписание всех тренеров</p>
                </div>
                <div className="period-controls">
                    <div className="calendar-export-controls">
                        <label className="calendar-export-all">
                            <input
                                type="checkbox"
                                checked={exportAllSchedule}
                                onChange={(e) => setExportAllSchedule(e.target.checked)}
                            />
                            Всё
                        </label>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleExportSchedule}
                            disabled={exporting || (!exportAllSchedule && !events?.length)}
                            title={
                                exportAllSchedule
                                    ? 'Скачать всё расписание за все время в Excel'
                                    : 'Скачать расписание за выбранный период в Excel'
                            }
                        >
                            <Download size={18} />
                            {exporting ? 'Выгрузка…' : 'Выгрузка'}
                        </button>
                    </div>
                    <button type="button" className="btn btn-secondary" onClick={goToPrevious3Months}>
                        <ChevronLeft size={18} />
                        Предыдущие 3 месяца
                    </button>
                    <span className="period-label">
                        {format(periodStart, 'LLLL yyyy', { locale: ru })} - {format(periodEnd, 'LLLL yyyy', { locale: ru })}
                    </span>
                    <button type="button" className="btn btn-secondary" onClick={goToNext3Months}>
                        Следующие 3 месяца
                        <ChevronRight size={18} />
                    </button>
                </div>
            </div>

            <div className="calendar-table-container">
                {trainers.length === 0 ? (
                    <div className="empty-state">
                        <CalendarIcon size={64} className="empty-icon" />
                        <h3>Нет данных для отображения</h3>
                        <p>Добавьте тренеров в расписание групп</p>
                    </div>
                ) : (
                    <table className="calendar-table">
                        <thead>
                            <tr>
                                <th className="trainer-column sticky-column">
                                    <div className="trainer-column-header">
                                        <button
                                            type="button"
                                            className={`trainer-sort-filter-btn ${trainerSortMode === 'occupancy' ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleTrainerSort();
                                            }}
                                            title={
                                                trainerSortMode === 'alpha'
                                                    ? 'Сортировка по алфавиту. Нажмите — по занятости в периоде'
                                                    : 'Сортировка по занятости. Нажмите — по алфавиту'
                                            }
                                        >
                                            Фильтр
                                        </button>
                                        <span className="trainer-column-title">Тренер</span>
                                    </div>
                                </th>
                                {days.map((day, idx) => (
                                    <th key={idx} className={`day-column ${format(day, 'EEEE', { locale: ru }) === 'суббота' || format(day, 'EEEE', { locale: ru }) === 'воскресенье' ? 'weekend' : ''}`}>
                                        <div className="day-header">
                                            <div className="day-number">{format(day, 'd')}</div>
                                            <div className="day-name">{format(day, 'EEE', { locale: ru })}</div>
                                            {format(day, 'd') === '1' && (
                                                <div className="month-label">{format(day, 'LLL', { locale: ru })}</div>
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {trainers.map(trainer => (
                                <tr
                                    key={trainer.id}
                                    className={selectedTrainerId === trainer.id ? 'selected-row' : ''}
                                    onClick={() => toggleTrainerSelection(trainer.id)}
                                >
                                    <td className="trainer-cell sticky-column">
                                        {trainer.name}
                                    </td>
                                    {days.map((day, dayIndex) => {
                                        const cellInfo = getCellSpanForDay(trainer.id, dayIndex);

                                        if (cellInfo?.skip) {
                                            return null; // Ячейка уже включена в colspan
                                        }

                                        if (cellInfo?.event) {
                                            const formatClass = getEventFormat(cellInfo.event);
                                            return (
                                                <td
                                                    key={dayIndex}
                                                    colSpan={cellInfo.span}
                                                    className={`event-cell event-format-${formatClass}`}
                                                    title={`${cellInfo.event.group_nomination || cellInfo.event.group_name}\n${cellInfo.event.region_name || ''}\n${cellInfo.event.theme}\nЦикл ${cellInfo.event.cycle}`}
                                                >
                                                    <div className="event-content">
                                                        <div className="event-group">{cellInfo.event.group_name}</div>
                                                        {cellInfo.event.region_name && (
                                                            <div className="event-region">{cellInfo.event.region_name}</div>
                                                        )}
                                                    </div>
                                                </td>
                                            );
                                        }

                                        if (cellInfo?.vacation) {
                                            return (
                                                <td
                                                    key={dayIndex}
                                                    colSpan={cellInfo.span}
                                                    className="vacation-cell"
                                                    title="Отпуск"
                                                >
                                                    <div className="event-content">
                                                        <div className="event-group">Отпуск</div>
                                                    </div>
                                                </td>
                                            );
                                        }

                                        return <td key={dayIndex} className="empty-cell"></td>;
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default CalendarPage;
